export interface DonationEmailData {
  donor_name: string;
  donor_email: string;
  donation_type: 'One-Time' | 'Monthly';
  donation_amount: number;
  donation_date: string;
  transaction_id: string;
  impact_message: string;
  is_monthly: boolean;
  website_url: string;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  unsubscribe_url?: string;
}

export class EmailTemplateService {
  private static generateImpactMessage(amount: number, isMonthly: boolean): string {
    const livesProtected = Math.floor(amount / 75);
    const timeframe = isMonthly ? 'each month' : '';
    
    if (livesProtected >= 1) {
      return `🎉 Your donation of R${amount} will protect approximately ${livesProtected} ${livesProtected === 1 ? 'life' : 'lives'} ${timeframe}!`;
    } else {
      return `Your generous contribution of R${amount} ${timeframe} brings us closer to protecting more lives from malaria.`;
    }
  }

  public static generateDonationConfirmationEmail(data: Partial<DonationEmailData>): DonationEmailData {
    const currentDate = new Date().toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const transactionId = data.transaction_id || `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    const emailData: DonationEmailData = {
      donor_name: data.donor_name || 'Valued Donor',
      donor_email: data.donor_email || '',
      donation_type: data.donation_type || 'One-Time',
      donation_amount: data.donation_amount || 0,
      donation_date: data.donation_date || currentDate,
      transaction_id: transactionId,
      impact_message: data.impact_message || this.generateImpactMessage(data.donation_amount || 0, data.is_monthly || false),
      is_monthly: data.is_monthly || false,
      website_url: data.website_url || 'https://goodbyemalaria.com',
      facebook_url: data.facebook_url || 'https://facebook.com/goodbyemalaria',
      twitter_url: data.twitter_url || 'https://twitter.com/goodbyemalaria',
      instagram_url: data.instagram_url || 'https://instagram.com/goodbyemalaria',
      linkedin_url: data.linkedin_url || 'https://linkedin.com/company/goodbyemalaria',
      unsubscribe_url: data.unsubscribe_url || 'https://goodbyemalaria.com/unsubscribe'
    };

    return emailData;
  }

  public static replaceTemplateVariables(template: string, data: DonationEmailData): string {
    let processedTemplate = template;

    // Replace all template variables
    Object.entries(data).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processedTemplate = processedTemplate.replace(regex, String(value));
    });

    // Handle conditional blocks for monthly donations
    if (data.is_monthly) {
      processedTemplate = processedTemplate.replace(/{{#if is_monthly}}([\s\S]*?){{\/if}}/g, '$1');
    } else {
      processedTemplate = processedTemplate.replace(/{{#if is_monthly}}([\s\S]*?){{\/if}}/g, '');
    }

    return processedTemplate;
  }

  public static async loadTemplate(templatePath: string): Promise<string> {
    try {
      // In a real application, you would load this from your file system or CDN
      // For now, we'll return a placeholder that indicates where to load the template
      const response = await fetch(templatePath);
      return await response.text();
    } catch (error) {
      console.error('Error loading email template:', error);
      throw new Error('Failed to load email template');
    }
  }
}

// Example usage function
export async function sendDonationConfirmationEmail(donationData: Partial<DonationEmailData>): Promise<void> {
  try {
    // Generate complete email data
    const emailData = EmailTemplateService.generateDonationConfirmationEmail(donationData);
    
    // Load the HTML template
    const templateHtml = await EmailTemplateService.loadTemplate('/templates/email/donation-confirmation.html');
    
    // Replace template variables
    const processedHtml = EmailTemplateService.replaceTemplateVariables(templateHtml, emailData);
    
    // Here you would integrate with your email service provider
    // Examples: SendGrid, Mailgun, AWS SES, etc.
    console.log('Sending email to:', emailData.donor_email);
    console.log('Email content generated successfully');
    
    // Example integration with email service:
    /*
    await emailService.send({
      to: emailData.donor_email,
      from: 'donations@goodbyemalaria.com',
      subject: 'Thank you for your donation to Goodbye Malaria',
      html: processedHtml
    });
    */
    
  } catch (error) {
    console.error('Error sending donation confirmation email:', error);
    throw error;
  }
}

// Helper function to format currency
export function formatCurrency(amount: number): string {
  return `R${amount.toFixed(2)}`;
}

// Helper function to format date
export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
